import { Flex } from '@chakra-ui/react';
import { faBell, faUser } from '@fortawesome/free-regular-svg-icons';
import {
  faChartLine,
  faQuoteLeft,
  faScrewdriverWrench,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { SnapQuoteStatus } from '@snap-types/models/enums';
import { PaginationParamsModel } from '@snap-types/models/pagination';
import { ModalLayout } from '@ui/components/layout/ModalLayout';
import { Navigation } from '@ui/components/layout/Navigation';
import { SitePageLayoutPlaceholder } from '@ui/components/layout/SiteLayout/SitePageLayoutPlaceholder';
import { WithPermission } from '@ui/components/Permission';
import { Show } from '@ui/components/Show';
import { Router } from '@ui/config/routes';
import { snapSiteConfigSelectors } from '@ui/features/snap-site-config/state';
import { useCurrentSiteAndTeamIds } from '@ui/hooks/useCurrentSiteAndTeamIds';
import { useCurrentUser } from '@ui/hooks/useCurrentUser';
import { useSelector } from '@ui/hooks/useSelector';
import { isFeatureVisibleToUser } from '@ui/util/is-feature-visible-to-user';
import { Suspense } from 'react';
import { Outlet } from 'react-router';

export function SiteLayout() {
  const { teamCode } = useCurrentSiteAndTeamIds();
  const { currentUser, isSuperAdmin } = useCurrentUser();

  const snapSiteConfig = useSelector((state) =>
    snapSiteConfigSelectors.selectSnapSiteConfig(state),
  );
  const partSearchVisibleToUser = isFeatureVisibleToUser(
    snapSiteConfig.partSearchVisible,
    isSuperAdmin,
  );

  return (
    <>
      <ModalLayout />

      <Flex direction="row">
        <Navigation.Menu>
          <Navigation.TeamSelector teamCode={teamCode} />

          <Navigation.Item
            label="Quote Queue"
            title={`Manage all ${teamCode} quotes`}
            path={Router.teamPath(
              teamCode,
              Router.routes.quoteQueue,
              undefined,
              PaginationParamsModel.defaultParams,
            )}
            icon={faQuoteLeft}
          />

          <Show when={partSearchVisibleToUser}>
            <Navigation.Item
              label="Part Search"
              title={'Search Parts'}
              path={Router.teamPath(teamCode, Router.routes.partSearch)}
              icon={faSearch}
            />
          </Show>

          <Navigation.Item
            label="My Quotes"
            title="Quotes created by you"
            path={Router.teamPath(
              teamCode,
              Router.routes.quoteQueue,
              undefined,
              {
                createdByDisplayName: currentUser.displayName,
                status: SnapQuoteStatus.INPROGRESS,
                ...PaginationParamsModel.defaultParams,
              },
            )}
            icon={faUser}
            matchSearch
          />

          <Navigation.Item
            label="My Assignments"
            title="Quotes assigned to you"
            path={Router.teamPath(
              teamCode,
              Router.routes.quoteQueue,
              undefined,
              {
                assignedToDisplayName: currentUser.displayName,
                status: SnapQuoteStatus.INPROGRESS,
                ...PaginationParamsModel.defaultParams,
              },
            )}
            icon={faBell}
            matchSearch
          />

          <WithPermission minimumRole={Router.routes.admin.minimumRole}>
            <Navigation.Divider />

            <Navigation.Item
              label="Admin"
              path={Router.path(Router.routes.admin)}
              icon={faScrewdriverWrench}
            />
          </WithPermission>

          <Navigation.Item
            label="Metrics"
            path={Router.path(Router.routes.metrics)}
            icon={faChartLine}
          />

          <Navigation.ResourceHubLink />
        </Navigation.Menu>

        {/* @NOTE(shawk): ensure <NavigationBar /> stays visible
            while descendent lazy-loaded routes are loading */}
        <Suspense fallback={<SitePageLayoutPlaceholder />}>
          <Outlet />
        </Suspense>
      </Flex>
    </>
  );
}
